/**
 * Input validation contract for /api/knowledge POST (text-entry form).
 *
 * Extracted from src/app/api/knowledge/route.ts as pure logic so the
 * product contract — required fields, size cap, type allowlist, error
 * wording, HTTP status codes — can be unit-tested deterministically
 * without Supabase, auth, or the embedding pipeline.
 *
 * The route handler delegates to validateKnowledgeEntryInput() and
 * preserves byte-for-byte the prior behavior.
 */

/** Allowed values for the `type` field on a knowledge_base entry. */
export const VALID_KNOWLEDGE_TYPES = [
  "faq",
  "info",
  "pricing",
  "hours",
  "menu",
  "policy",
  "other",
  "document",
] as const;

export type KnowledgeType = (typeof VALID_KNOWLEDGE_TYPES)[number];

/**
 * Normalize a UI knowledge type to the database-safe value.
 *
 * The product UI still exposes "document" for business-doc flows, but the
 * current production knowledge_base check constraint does not allow it.
 * We store those entries as "info" so inserts remain valid without
 * broadening the schema contract.
 */
export function normalizeKnowledgeTypeForDb(type: KnowledgeType): Exclude<KnowledgeType, "document"> {
  return type === "document" ? "info" : type;
}

/**
 * Cleans extracted knowledge text before it is stored, embedded, or
 * appended back into an agent prompt.
 *
 * The sanitizer keeps readable punctuation and currency symbols while
 * removing control characters and noisy divider lines that often confuse
 * downstream answer extraction.
 */
export function sanitizeExtractedKnowledgeText(text: string | null | undefined): string {
  const normalized = (text ?? "")
    .normalize("NFKC")
    .replace(/\u0000/g, "")
    .replace(/[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, " ")
    .replace(/[=\-_*#]{3,}/g, " ")
    .replace(/\r\n?/g, "\n");

  const cleanedLines = normalized
    .split("\n")
    .map((line) =>
      line
        .replace(/^[\s=\-_*#]+/, "")
        .replace(/[\s=\-_*#]+$/, "")
        .replace(/[ \t]{2,}/g, " ")
        .trim()
    )
    .filter((line) => line.length > 0)
    .filter((line) => !/^[=\-_*#\s]+$/.test(line));

  return cleanedLines
    .join("\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Upper bound on the `content` field for a single text entry: 500 KB. */
export const MAX_KNOWLEDGE_CONTENT_BYTES = 500 * 1024;

export interface KnowledgeEntryInput {
  agentId: string;
  title: string;
  content: string;
  type: KnowledgeType;
}

export type ValidationResult =
  | { ok: true; value: KnowledgeEntryInput }
  | { ok: false; status: number; error: string };

/**
 * Validates the JSON body of a POST /api/knowledge text entry.
 *
 * Order of checks (stable, documented contract):
 *   1. All four fields present  → 400 if missing
 *   2. Content size ≤ 500 KB    → 413 if exceeded
 *   3. Type in allowlist        → 400 if not
 */
export function validateKnowledgeEntryInput(body: unknown): ValidationResult {
  const b = (body ?? {}) as Record<string, unknown>;
  const agentId = typeof b.agentId === "string" ? b.agentId : "";
  const title = typeof b.title === "string" ? b.title : "";
  const content = typeof b.content === "string" ? b.content : "";
  const type = typeof b.type === "string" ? b.type : "";

  if (!agentId || !title || !content || !type) {
    return {
      ok: false,
      status: 400,
      error: "agentId, title, content, and type are required",
    };
  }

  // Compute byte length the same way the route does (Buffer.byteLength
  // in Node, TextEncoder.encode().length in the edge runtime — both
  // count UTF-8 bytes).
  const byteLength =
    typeof Buffer !== "undefined"
      ? Buffer.byteLength(content, "utf8")
      : new TextEncoder().encode(content).length;

  if (byteLength > MAX_KNOWLEDGE_CONTENT_BYTES) {
    return {
      ok: false,
      status: 413,
      error:
        "Content exceeds the maximum allowed size of 500 KB. Please split it into smaller entries.",
    };
  }

  if (!(VALID_KNOWLEDGE_TYPES as readonly string[]).includes(type)) {
    return {
      ok: false,
      status: 400,
      error: `Invalid type. Must be one of: ${VALID_KNOWLEDGE_TYPES.join(", ")}`,
    };
  }

  return {
    ok: true,
    value: { agentId, title, content, type: type as KnowledgeType },
  };
}

// ============================================================================
// Upload-side validation — POST /api/knowledge (multipart/form-data)
// ============================================================================

/** File extensions accepted by the knowledge-base file upload path. */
const CORE_SUPPORTED_UPLOAD_EXTENSIONS = [".pdf", ".txt", ".md", ".csv", ".docx"] as const;

export const SUPPORTED_UPLOAD_EXTENSIONS = [
  ...CORE_SUPPORTED_UPLOAD_EXTENSIONS,
  ".doc",
] as const;

export type SupportedUploadExtension = (typeof SUPPORTED_UPLOAD_EXTENSIONS)[number];

/** Shared accept-string for file inputs that should mirror the validator. */
export function getKnowledgeUploadAccept(): string {
  return SUPPORTED_UPLOAD_EXTENSIONS.join(",");
}

/** Human-readable label for upload help text. */
export function getKnowledgeUploadLabel(): string {
  return CORE_SUPPORTED_UPLOAD_EXTENSIONS
    .map((ext) => ext.replace(/^\./, "").toUpperCase())
    .join(", ");
}

export function getKnowledgeUploadSupportCopy(): string {
  return `Supported file formats: ${getKnowledgeUploadLabel()}. Legacy DOC is accepted as best-effort; DOCX is recommended for best accuracy.`;
}

export const LEGACY_DOC_SELECTED_WARNING =
  "Legacy DOC selected. We'll try to read it, but formatting may be incomplete. For best results, save this file as DOCX and upload again.";

export const LEGACY_DOC_IMPORTED_MESSAGE =
  "Legacy DOC imported. Please review the extracted text. DOCX is recommended for best accuracy.";

export const LEGACY_DOC_FAILURE_MESSAGE =
  "This legacy DOC file could not be read cleanly. Please save it as DOCX, PDF, TXT, MD, or CSV and upload again.";

export const PDF_PARSE_FAILURE_MESSAGE =
  "We could not read this PDF safely. Please try exporting it again as a text-based PDF, or upload DOCX, TXT, MD, or CSV.";

export const PDF_NO_READABLE_TEXT_MESSAGE =
  "This PDF does not contain readable text. Please upload a text-based PDF, DOCX, TXT, MD, or CSV file.";

export interface ExtractedKnowledgeTextQuality {
  ok: boolean;
  reason:
    | "empty"
    | "too_short"
    | "high_replacement_ratio"
    | "high_control_ratio"
    | "low_printable_ratio"
    | "binary_markers";
  textLength: number;
  trimmedLength: number;
  printableRatio: number;
  replacementRatio: number;
  controlRatio: number;
  wordCount: number;
  hasBinaryMarkers: boolean;
}

const BINARY_MARKER_PATTERN =
  /\b(?:root entry|worddocument|compobj|ole10native|msole|ole object|microsoft ole|package stream|docfile)\b/i;

function countMatches(text: string, pattern: RegExp): number {
  const matches = text.match(pattern);
  return matches ? matches.length : 0;
}

export function assessExtractedKnowledgeTextQuality(
  text: string | null | undefined
): ExtractedKnowledgeTextQuality {
  const normalized = (text ?? "").normalize("NFKC").replace(/\r\n?/g, "\n");
  const trimmed = normalized.trim();
  const textLength = normalized.length;
  const trimmedLength = trimmed.length;
  const printableChars = normalized.replace(/[^\p{L}\p{N}\p{P}\p{S}\s]/gu, "");
  const printableRatio = textLength > 0 ? printableChars.length / textLength : 0;
  const replacementRatio = textLength > 0 ? countMatches(normalized, /\uFFFD/g) / textLength : 0;
  const controlRatio = textLength > 0
    ? countMatches(normalized, /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g) / textLength
    : 0;
  const wordCount = countMatches(normalized, /\b[\p{L}]{3,}\b/gu);
  const hasBinaryMarkers = BINARY_MARKER_PATTERN.test(normalized);

  if (trimmedLength === 0) {
    return {
      ok: false,
      reason: "empty",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  if (trimmedLength < MIN_EXTRACTED_TEXT_LENGTH) {
    return {
      ok: false,
      reason: "too_short",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  if (hasBinaryMarkers) {
    return {
      ok: false,
      reason: "binary_markers",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  if (replacementRatio > 0.01) {
    return {
      ok: false,
      reason: "high_replacement_ratio",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  if (controlRatio > 0.03) {
    return {
      ok: false,
      reason: "high_control_ratio",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  if (printableRatio < 0.65 || wordCount < 2) {
    return {
      ok: false,
      reason: "low_printable_ratio",
      textLength,
      trimmedLength,
      printableRatio,
      replacementRatio,
      controlRatio,
      wordCount,
      hasBinaryMarkers,
    };
  }

  return {
    ok: true,
    reason: "empty",
    textLength,
    trimmedLength,
    printableRatio,
    replacementRatio,
    controlRatio,
    wordCount,
    hasBinaryMarkers,
  };
}

/** Upper bound on uploaded file size: 10 MB. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Minimal shape we need from a File/Blob-like input for validation. */
export interface UploadFileLike {
  name: string;
  size: number;
}

export interface KnowledgeUploadInput {
  file: UploadFileLike;
  agentId: string;
  ext: SupportedUploadExtension;
}

export type UploadValidationResult =
  | { ok: true; value: KnowledgeUploadInput }
  | { ok: false; status: number; error: string };

/**
 * Extracts a lowercased file extension (including the leading dot) from a
 * file name. Returns an empty string when no extension is present, so the
 * allowlist check rejects dotfiles and extensionless names cleanly.
 *
 * Examples:
 *   "dental.pdf"     → ".pdf"
 *   "Menu.TXT"       → ".txt"
 *   "virus.txt.exe"  → ".exe"   (double-extension: only the final counts)
 *   "plain-name"     → ""
 *   ".env"           → ""       (leading dot, no real extension)
 */
export function extractFileExtension(fileName: string): string {
  const lower = (fileName || "").toLowerCase();
  const lastDot = lower.lastIndexOf(".");
  // No dot, or dot is at position 0 (dotfile with no real extension)
  if (lastDot <= 0) return "";
  return lower.slice(lastDot);
}

/**
 * Validates the multipart inputs for POST /api/knowledge uploads.
 *
 * Order of checks (stable, documented contract):
 *   1. file + agentId both present → 400 if either missing
 *   2. extension in allowlist      → 400 if not
 *   3. size ≤ 10 MB                → 400 if over
 *
 * Returns a normalized `ext` (lowercased, dotted) on success so callers
 * don't repeat the extraction logic.
 */
export function validateKnowledgeUploadInput(input: {
  file: UploadFileLike | null | undefined;
  agentId: string | null | undefined;
}): UploadValidationResult {
  const { file, agentId } = input;

  if (!file || !agentId) {
    return {
      ok: false,
      status: 400,
      error: "file and agentId are required",
    };
  }

  const ext = extractFileExtension(file.name);

  if (!(SUPPORTED_UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) {
    return {
      ok: false,
      status: 400,
      error: `Unsupported file type. Supported: ${SUPPORTED_UPLOAD_EXTENSIONS.join(", ")}`,
    };
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      status: 400,
      error: "File size must be under 10MB",
    };
  }

  return {
    ok: true,
    value: { file, agentId, ext: ext as SupportedUploadExtension },
  };
}

// ============================================================================
// Post-extraction finalization — once a file is accepted and its text has
// been extracted (via pdf-parse, mammoth, or plain UTF-8 decode), three
// pure decisions remain: is the text usable, how is it capped, and how is
// its parent title derived from the original filename. These are extracted
// so they can be tested without running the extractors themselves.
// ============================================================================

/**
 * Minimum usable length of extracted text after trimming, in characters.
 * Anything shorter is treated as an extraction failure (empty file,
 * image-only PDF, corrupt DOCX, etc.).
 */
export const MIN_EXTRACTED_TEXT_LENGTH = 10;

/** Upper bound on the stored `content` column for a document entry. */
export const MAX_STORED_CONTENT_CHARS = 50_000;

/** User-facing error when extracted text is empty, whitespace, or too short. */
export const EXTRACTION_FAILURE_ERROR =
  "Could not extract text from file. The file may be empty or in an unsupported format.";

/**
 * Derives the parent knowledge-entry title from a filename by stripping
 * the FINAL dotted extension. Matches the behavior previously inlined in
 * handleFileUpload():  file.name.replace(/\.[^.]+$/, "").
 *
 * Case is preserved — the extension regex is case-insensitive but the
 * title keeps the caller's original spelling.
 *
 * Examples:
 *   "menu.pdf"        → "menu"
 *   "REPORT.PDF"      → "REPORT"
 *   "a.b.c.doc"       → "a.b.c"
 *   "archive.tar.gz"  → "archive.tar"
 *   "notes"           → "notes"   (no extension → unchanged)
 */
export function deriveTitleFromFilename(fileName: string): string {
  return (fileName || "").replace(/\.[^.]+$/, "");
}

export type FinalizeExtractedTextResult =
  | { ok: true; title: string; content: string }
  | { ok: false; status: 400; error: string };

/**
 * Given an original filename and the raw text returned by an extractor,
 * returns the finalized {title, content} pair ready for DB insert — or a
 * 400 error if the text is empty, whitespace-only, or shorter than the
 * minimum meaningful length. Content is truncated to MAX_STORED_CONTENT_CHARS.
 *
 * Guarantees for the caller:
 *   - On ok:true, `content.length <= MAX_STORED_CONTENT_CHARS`.
 *   - On ok:true, `title` is the filename with the last extension stripped.
 *   - The order of failure is: empty/whitespace/too-short → 400. Truncation
 *     never creates a failure; it only shortens.
 */
export function finalizeExtractedText(input: {
  fileName: string;
  text: string | null | undefined;
}): FinalizeExtractedTextResult {
  const raw = sanitizeExtractedKnowledgeText(input.text);
  if (!raw || raw.trim().length < MIN_EXTRACTED_TEXT_LENGTH) {
    return { ok: false, status: 400, error: EXTRACTION_FAILURE_ERROR };
  }

  return {
    ok: true,
    title: deriveTitleFromFilename(input.fileName),
    content: raw.slice(0, MAX_STORED_CONTENT_CHARS),
  };
}
